import { useMemo, useRef } from 'react';
import { Dicom } from '@/models/dicom';
import { ViewerPane } from '../ViewerPane';
import { useViewerContext } from '../ViewerContext';
import { useViewportResize } from '../hooks/useViewportResize';
import styles from './ViewerGrid.module.css';
import type { GridLayout } from '../ViewerContext';

interface ViewerGridProps {
  gridLayout: GridLayout;
  dicoms: (Dicom | null)[];
  onViewportClick: (viewportIndex: number) => void;
  selectedViewportIndex: number;
  pinnedPanes: ReadonlyMap<number, Dicom>;
  onPinPane: (paneIndex: number) => void;
  onUnpinPane: (paneIndex: number) => void;
  onPlaceDicom: (paneIndex: number, dicom: Dicom) => void;
}

export function ViewerGrid({
  gridLayout,
  dicoms,
  onViewportClick,
  selectedViewportIndex,
  pinnedPanes,
  onPinPane,
  onUnpinPane,
  onPlaceDicom,
}: ViewerGridProps) {
  const viewportCount = gridLayout.rows * gridLayout.cols;
  const { renderingEngine } = useViewerContext();
  const gridRef = useRef<HTMLDivElement>(null);

  // Single resize coordinator for the whole grid. renderingEngine.resize() is
  // global (resizes every viewport at once), so one observer on the grid
  // container replaces the previous per-pane observers — which fired N
  // redundant resize() calls per frame and tripped the engine's dropped-resize
  // path with multiple viewports.
  useViewportResize({ containerRef: gridRef, renderingEngine });

  const viewportSlots = useMemo(() => {
    const slots = Array.from({ length: viewportCount }, (_, index) => ({
      viewportId: `viewport-${index}`,
      dicom: dicoms[index] ?? null,
      paneIndex: index,
    }));
    return slots;
  }, [viewportCount, dicoms]);

  return (
    <div
      ref={gridRef}
      className={styles.viewerGrid}
      style={{
        gridTemplateRows: `repeat(${gridLayout.rows}, 1fr)`,
        gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)`,
      }}
    >
      {viewportSlots.map(({ viewportId, dicom, paneIndex }) => (
        <ViewerPane
          key={viewportId}
          viewportId={viewportId}
          dicom={dicom}
          paneIndex={paneIndex}
          onClick={() => onViewportClick(paneIndex)}
          isSelected={paneIndex === selectedViewportIndex}
          isPinned={pinnedPanes.has(paneIndex)}
          onUnpin={() => onUnpinPane(paneIndex)}
          onPin={dicom ? () => onPinPane(paneIndex) : undefined}
          onDropDicom={(d) => onPlaceDicom(paneIndex, d)}
        />
      ))}
    </div>
  );
}
